import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { LiveBanner } from './components/LiveBanner';
import { HalloweenModal } from './components/HalloweenModal';
import { HalloweenDecorations } from './components/HalloweenDecorations';
import { HALLOWEEN_ENABLED } from './config/halloween';
import './halloween.css';
import { AmbientParticles, ScrollProgress } from './components/Effects';
import { HomePage } from './pages/HomePage';
import { EconomyPage } from './pages/EconomyPage';
import { RulesPage } from './pages/RulesPage';
import { ProfilePage } from './pages/ProfilePage';
import { AboutPage } from './pages/AboutPage';
import { JourneyPage } from './pages/JourneyPage';
import { NewsPage } from './pages/NewsPage';
import { EventsPage } from './pages/EventsPage';
import { HalloweenEventPage } from './pages/HalloweenEventPage';
import './events.css';
import { ApplyPage } from './pages/ApplyPage';
import { useExperience } from './hooks/useExperience';
import type { SessionUser } from './types';

export function App() {
  const location = useLocation();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  useExperience(location.pathname);

  useEffect(() => {
    if (HALLOWEEN_ENABLED) {
      document.body.classList.add('halloween-active');
    } else {
      document.body.classList.remove('halloween-active');
    }
    return () => {
      document.body.classList.remove('halloween-active');
    };
  }, []);

  useEffect(() => {
    fetch('/api/me').then((response) => response.json()).then((data) => setUser(data.user || null)).catch(() => setUser(null)).finally(() => setLoading(false));
  }, []);
  useEffect(() => {
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const hostname = window.location.hostname.toLowerCase();
  const rulesHost = hostname.startsWith('rules.');
  const applyHost = hostname.startsWith('apply.');

  // If a user navigates to an extraneous route while on apply or rules subdomain, redirect them to the main domain
  useEffect(() => {
    if (applyHost && location.pathname !== '/' && location.pathname !== '/apply') {
      window.location.replace(`https://ugeappagesmp.online${location.pathname}`);
    } else if (rulesHost && location.pathname !== '/' && location.pathname !== '/rules') {
      window.location.replace(`https://ugeappagesmp.online${location.pathname}`);
    }
  }, [applyHost, rulesHost, location.pathname]);

  return (
    <div className="app-shell">
      <div className="noise" aria-hidden="true" />
      <AmbientParticles />
      <ScrollProgress />
      {/* Top Banner when a YouTube Live is active */}
      <LiveBanner />
      <Navbar user={user} loading={loading} />
      {HALLOWEEN_ENABLED && (
        <>
          <HalloweenDecorations />
          <HalloweenModal user={user} loadingUser={loading} />
        </>
      )}
      <main>
        <Routes>
          <Route
            path="/"
            element={
              rulesHost ? (
                <Navigate to="/rules" replace />
              ) : applyHost ? (
                <ApplyPage user={user} loading={loading} onUserChange={setUser} />
              ) : (
                <HomePage user={user} loading={loading} onUserChange={setUser} />
              )
            }
          />
          <Route path="/apply" element={<ApplyPage user={user} loading={loading} onUserChange={setUser} />} />
          <Route path="/economy" element={<EconomyPage />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:id" element={<NewsPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/halloween-2026" element={<HalloweenEventPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/journey" element={<JourneyPage />} />
          <Route path="/profile" element={<ProfilePage user={user} loadingUser={loading} onUserChange={setUser} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}
